import { FilterSettings, TemplateCommunitySortBy } from '@/types';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { PATIENT_SUMMARY_TEMPLATE_CATEGORY } from '@heidi/patients/contracts';
import { type TranslationKey } from '@heidi/runtime/i18n';
import {
  ConsultStorageDays,
  DictationCleanupModes,
  DocumentGenType,
  ExtendFreeTrialHours,
  GenerationMethod,
  LanguageCode,
  MFAMethod,
  NoteStructure,
  OrganisationRole,
  OrganisationSize,
  SessionStatus,
  SpellingPreference,
  SubscriptionTier,
  TeamDisclaimerFooterScope,
  TemplateCategory,
  TitleCode,
  TranscribeMode,
  UserRole,
  VoiceStyle,
} from '@heidi/schema/ml-scribe';

type EnumUnion =
  | VoiceStyle
  | NoteStructure
  | TranscribeMode
  | LanguageCode
  | TitleCode
  | ConsultStorageDays
  | GenerationMethod
  | DocumentGenType
  | TemplateCategory
  | OrganisationSize
  | OrganisationRole
  | TemplateCommunitySortBy
  | FilterSettings
  | DictationCleanupModes
  | SubscriptionTier
  | SpellingPreference
  | UserRole
  | ExtendFreeTrialHours
  | TeamDisclaimerFooterScope
  | MFAMethod
  | SessionStatus;

export const enumToDescription: Record<EnumUnion, string> = {
  [VoiceStyle.MY_VOICE]: '',
  [VoiceStyle.GOLDILOCKS]: 'Goldilocks',
  [VoiceStyle.DETAILED]: 'Detailed',
  [VoiceStyle.BRIEF]: 'Brief',
  [VoiceStyle.SUPER_DETAILED]: 'Super Detailed',

  [NoteStructure.HOPC]: 'HOPC',
  [NoteStructure.SOAP]: 'SOAP',
  [NoteStructure.MY_STRUCTURE]: getEnvironment() === ENV_NAME.PROD ? '' : 'My Structure',
  [NoteStructure.HOPC_ISSUES_LIST_]: 'HOPC (Issues)',
  [NoteStructure.SOAP_ISSUES_LIST_]: 'SOAP (Issues)',
  [NoteStructure.ISSUES_LIST]: 'Issues Only',
  [NoteStructure.DEFAULT]: 'Default',

  [TranscribeMode.TRANSCRIBE]: 'Transcribe',
  [TranscribeMode.DICTATE]: 'Dictate',
  [TranscribeMode.TELEHEALTH]: 'Telehealth',

  [LanguageCode.auto]: 'Auto-detect',
  [LanguageCode.en]: 'English',
  [LanguageCode.en_US]: 'English (US)',
  [LanguageCode.en_GB]: 'English (GB)',
  [LanguageCode.en_AU]: 'English (AU)',
  [LanguageCode.en_IN]: 'English (IN)',
  [LanguageCode.en_NZ]: 'English (NZ)',
  [LanguageCode.cs]: 'Czech',
  [LanguageCode.da]: 'Danish',
  [LanguageCode.da_DK]: 'Danish (DK)',
  [LanguageCode.nl]: 'Dutch',
  [LanguageCode.nl_BE]: 'Flemish',
  [LanguageCode.fr]: 'French',
  [LanguageCode.fr_CA]: 'French (Canada)',
  [LanguageCode.de]: 'German',
  [LanguageCode.de_CH]: 'German (Switzerland)',
  [LanguageCode.el]: 'Greek',
  [LanguageCode.hi]: 'Hindi',
  [LanguageCode.hi_Latn]: 'Hindi (Latin)',
  [LanguageCode.id]: 'Indonesian',
  [LanguageCode.ja]: 'Japanese',
  [LanguageCode.it]: 'Italian',
  [LanguageCode.ko]: 'Korean',
  [LanguageCode.ko_KR]: 'Korean (KR)',
  [LanguageCode.no]: 'Norwegian',
  [LanguageCode.pl]: 'Polish',
  [LanguageCode.pt]: 'Portuguese',
  [LanguageCode.pt_BR]: 'Portuguese (Brazil)',
  [LanguageCode.ru]: 'Russian',
  [LanguageCode.es]: 'Spanish',
  [LanguageCode.es_419]: 'Spanish (Latin America)',
  [LanguageCode.sv]: 'Swedish',
  [LanguageCode.sv_SE]: 'Swedish (SE)',
  [LanguageCode.tr]: 'Turkish',
  [LanguageCode.uk]: 'Ukrainian',
  [LanguageCode.bg]: 'Bulgarian',
  [LanguageCode.et]: 'Estonian',
  [LanguageCode.hu]: 'Hungarian',
  [LanguageCode.lv]: 'Latvian',
  [LanguageCode.lt]: 'Lithuanian',
  [LanguageCode.ms]: 'Malay',
  [LanguageCode.ro]: 'Romanian',
  [LanguageCode.sk]: 'Slovak',
  [LanguageCode.th]: 'Thai',
  [LanguageCode.th_TH]: 'Thai (Thailand)',
  [LanguageCode.vi]: 'Vietnamese',
  [LanguageCode.ar_AE]: 'Arabic (UAE)',
  [LanguageCode.ar_SA]: 'Arabic (Saudi Arabia)',
  [LanguageCode.ca]: 'Catalan',
  [LanguageCode.zh]: 'Chinese',
  [LanguageCode.zh_HK]: 'Cantonese',
  [LanguageCode.zh_Hant]: 'Chinese (Traditional)',
  [LanguageCode.fi]: 'Finnish',
  [LanguageCode.multi]: 'Multilingual',

  // New languages understood by all gemini models
  [LanguageCode.af]: 'Afrikaans',
  [LanguageCode.ar]: 'Arabic',
  [LanguageCode.hy]: 'Armenian',
  [LanguageCode.az]: 'Azerbaijani',
  [LanguageCode.be]: 'Belarusian',
  [LanguageCode.bs]: 'Bosnian',
  [LanguageCode.hr]: 'Croatian',
  [LanguageCode.gl]: 'Galician',
  [LanguageCode.is]: 'Icelandic',
  [LanguageCode.kn]: 'Kannada',
  [LanguageCode.kk]: 'Kazakh',
  [LanguageCode.mk]: 'Macedonian',
  [LanguageCode.mi]: 'Te Reo Māori',
  [LanguageCode.mr]: 'Marathi',
  [LanguageCode.ne]: 'Nepali',
  [LanguageCode.fa]: 'Persian',
  [LanguageCode.sr]: 'Serbian',
  [LanguageCode.sl]: 'Slovenian',
  [LanguageCode.sw]: 'Swahili',
  [LanguageCode.ta]: 'Tamil',
  [LanguageCode.ur]: 'Urdu',
  [LanguageCode.cy]: 'Welsh',

  [LanguageCode.bn]: 'Bengali',
  [LanguageCode.iw]: 'Hebrew', // Note: 'iw' is an older code for Hebrew

  // Additional Gemini supported languages only understood by Gemini 1.5 Pro, 1.5 Flash, and 2.0 Flash
  [LanguageCode.am]: 'Amharic',
  [LanguageCode.as]: 'Assamese',
  [LanguageCode.ceb]: 'Cebuano',
  [LanguageCode.co]: 'Corsican',
  [LanguageCode.dv]: 'Dhivehi',
  [LanguageCode.eo]: 'Esperanto',
  [LanguageCode.eu]: 'Basque',
  [LanguageCode.fil]: 'Filipino (Tagalog)',
  [LanguageCode.fy]: 'Frisian',
  [LanguageCode.ga]: 'Irish',
  [LanguageCode.gd]: 'Scots Gaelic',
  [LanguageCode.gu]: 'Gujarati',
  [LanguageCode.ha]: 'Hausa',
  [LanguageCode.haw]: 'Hawaiian',
  [LanguageCode.hmn]: 'Hmong',
  [LanguageCode.ht]: 'Haitian Creole',
  [LanguageCode.ig]: 'Igbo',
  [LanguageCode.jv]: 'Javanese',
  [LanguageCode.ka]: 'Georgian',
  [LanguageCode.km]: 'Khmer',
  [LanguageCode.kri]: 'Krio',
  [LanguageCode.ku]: 'Kurdish',
  [LanguageCode.ky]: 'Kyrgyz',
  [LanguageCode.la]: 'Latin',
  [LanguageCode.lb]: 'Luxembourgish',
  [LanguageCode.lo]: 'Lao',
  [LanguageCode.mg]: 'Malagasy',
  [LanguageCode.ml]: 'Malayalam',
  [LanguageCode.mn]: 'Mongolian',
  [LanguageCode.mni_Mtei]: 'Meiteilon (Manipuri)',
  [LanguageCode.mt]: 'Maltese',
  [LanguageCode.my]: 'Myanmar (Burmese)',
  [LanguageCode.ny]: 'Nyanja (Chichewa)',
  [LanguageCode.or]: 'Odia (Oriya)',
  [LanguageCode.pa]: 'Punjabi',
  [LanguageCode.ps]: 'Pashto',
  [LanguageCode.sd]: 'Sindhi',
  [LanguageCode.si]: 'Sinhala (Sinhalese)',
  [LanguageCode.sm]: 'Samoan',
  [LanguageCode.sn]: 'Shona',
  [LanguageCode.so]: 'Somali',
  [LanguageCode.sq]: 'Albanian',
  [LanguageCode.st]: 'Sesotho',
  [LanguageCode.su]: 'Sundanese',
  [LanguageCode.te]: 'Telugu',
  [LanguageCode.to]: 'Tongan',
  [LanguageCode.tg]: 'Tajik',
  [LanguageCode.ug]: 'Uyghur',
  [LanguageCode.uz]: 'Uzbek',
  [LanguageCode.xh]: 'Xhosa',
  [LanguageCode.yi]: 'Yiddish',
  [LanguageCode.yo]: 'Yoruba',
  [LanguageCode.zu]: 'Zulu',
  [LanguageCode.tkl]: 'Tokelauan',
  [LanguageCode.tvl]: 'Tuvaluan',
  [LanguageCode.rar]: 'Cook Island Maori',
  [LanguageCode.fij]: 'Fijian',
  [LanguageCode.hif]: 'Fiji Hindi',
  [LanguageCode.rtm]: 'Rotuman',

  [TitleCode.Mr_]: 'Mr',
  [TitleCode.Mrs_]: 'Mrs',
  [TitleCode.Ms_]: 'Ms',
  [TitleCode.Miss]: 'Miss',
  [TitleCode.Dr_]: 'Dr',
  [TitleCode.Prof_]: 'Prof',
  [ConsultStorageDays.Value7_days]: '7 days',
  [ConsultStorageDays.Value3_days]: '3 days',
  [ConsultStorageDays.Value1_day]: '1 days',
  [ConsultStorageDays.Value21_days]: '21 days',
  [ConsultStorageDays.Value90_days]: '90 days',
  [ConsultStorageDays.Never_Delete]: 'Never Delete',

  [GenerationMethod.AI_COMMAND_TEXT]: 'AI prompt',
  [GenerationMethod.TEMPLATE]: 'Template',
  [GenerationMethod.PRESET_AI_COMMAND]: 'AI prompt',
  [GenerationMethod.THIRD_PARTY_TEMPLATE]: 'Third party template',
  [GenerationMethod.LIVE_DICTATION]: 'Live dictation',
  [GenerationMethod.PDF]: 'PDF template',
  [GenerationMethod.ASK_AI]: 'AI prompt',
  [GenerationMethod.DICTATION]: 'Dictation',
  [GenerationMethod.GPCCMP]: 'GPCCMP',
  [GenerationMethod.AUTO_MODE]: 'Auto',
  [GenerationMethod.BLANK]: 'Blank document',
  [DocumentGenType.INTERNAL_TEMPLATE]: 'Internal Template',
  // [GenerationMethod.NONE]: 'None',

  [TemplateCategory.CONSULT_NOTE_TEMPLATE]: 'Note',
  [TemplateCategory.DOCUMENT_TEMPLATE]: 'Document',
  [TemplateCategory.FORM_TEMPLATE]: 'Form',
  [TemplateCategory.LETTER_TEMPLATE]: 'Letter',
  [TemplateCategory.ASSESSMENT_TEMPLATE]: 'Assessment',
  [TemplateCategory.PRECHART_TEMPLATE]: 'Prechart',
  [PATIENT_SUMMARY_TEMPLATE_CATEGORY]: 'Patient Summary',

  [OrganisationRole.EXECUTIVE_TEAM]: 'Executive team (CIO, CMIO, CEO etc.)',
  [OrganisationRole.INDIVIDUAL_CLINICIAN]: 'Individual clinician',
  [OrganisationRole.LEAD]: 'Clinical lead, department lead, head of …',
  [OrganisationRole.OWNER_OR_PRACTICE_MANAGER]: 'Owner or practice manager',

  [OrganisationSize.FIFTY_ONE_PLUS]: '51+',
  [OrganisationSize.TWO_TO_FIVE]: '2-5',
  [OrganisationSize.SIX_TO_TWENTY]: '6-20',
  [OrganisationSize.TWENTY_ONE_TO_FIFTY]: '21-50',
  [OrganisationSize.JUST_ME]: 'Just me',

  [TemplateCommunitySortBy.MOST_POPULAR]: 'Most popular',
  [TemplateCommunitySortBy.MOST_RECENT]: 'Most recent',
  [TemplateCommunitySortBy.ALPHABETICAL]: 'Alphabetical',

  [DictationCleanupModes.SMART]: 'Smart dictation',
  [DictationCleanupModes.WORD_FOR_WORD]: 'Word-for-word',

  [FilterSettings.ALL]: 'All',
  [FilterSettings.UNDEFINED]: 'Undefined',
  [FilterSettings.INITIAL]: 'Initial',

  [SubscriptionTier.FREE]: 'Free',
  [SubscriptionTier.EVIDENCE]: 'Evidence',
  [SubscriptionTier.EVIDENCE_TEAM]: 'Evidence Team',
  [SubscriptionTier.PRO]: 'Pro',
  [SubscriptionTier.CLINICIAN_PRO]: 'Clinician',
  [SubscriptionTier.PRO_PLUS]: 'Practice',
  [SubscriptionTier.CLINICIAN_PRO_PLUS]: 'Practice',
  [SubscriptionTier.ENTERPRISE]: 'Enterprise',
  [SubscriptionTier.CLINICIAN_ENTERPRISE]: 'Enterprise',

  [SpellingPreference.UK]: 'UK English',
  [SpellingPreference.US]: 'US English',
  [SpellingPreference.AU]: 'Australian English',
  [SpellingPreference.CA]: 'Canadian English',

  [ExtendFreeTrialHours.Value1]: '1 hour',
  [ExtendFreeTrialHours.Value2]: '2 hours',
  [ExtendFreeTrialHours.Value4]: '4 hours',
  [ExtendFreeTrialHours.Value6]: '6 hours',
  [ExtendFreeTrialHours.Value12]: '12 hours',
  [ExtendFreeTrialHours.Value24]: '24 hours',

  [UserRole.ADMIN]: 'Administrator',
  [UserRole.MEMBER]: 'Clinician',

  [TeamDisclaimerFooterScope.NOTE]: 'Note',
  [TeamDisclaimerFooterScope.DOCUMENT]: 'Document',

  [MFAMethod.MFA_SMS_EMAIL]: 'SMS and email authentication',
  [MFAMethod.MFA_SMS]: 'SMS authentication',

  [SessionStatus.EMPTY]: 'noStatus',
  [SessionStatus.DRAFT]: 'draft',
  [SessionStatus.FOR_ACTION]: 'forAction',
  [SessionStatus.REVIEWED]: 'reviewed',
  [SessionStatus.APPROVED]: 'approved',
  [SessionStatus.SENT]: 'sent',
};

export const getTitleTranslationKey = (title: TitleCode): TranslationKey => {
  const titleToKeyMap: Record<TitleCode, TranslationKey> = {
    [TitleCode.Mr_]: 'titles.Mr',
    [TitleCode.Mrs_]: 'titles.Mrs',
    [TitleCode.Ms_]: 'titles.Ms',
    [TitleCode.Miss]: 'titles.Miss',
    [TitleCode.Dr_]: 'titles.Dr',
    [TitleCode.Prof_]: 'titles.Prof',
  };
  return titleToKeyMap[title];
};
